import * as React from "react";
import { cn } from "@/lib/utils";

interface ProgressProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'max'> {
  value: number;
  max?: number;
  label?: string;
  showValue?: boolean;
  size?: "sm" | "md" | "lg";
  variant?: "default" | "success" | "warning" | "danger";
  indicatorClassName?: string;
}

const sizeClasses = {
  sm: "h-1.5",
  md: "h-2",
  lg: "h-3",
};

const variantClasses = {
  default: "bg-primary",
  success: "bg-success",
  warning: "bg-warning",
  danger: "bg-destructive",
};

function getVariantFromPercentage(percentage: number): "default" | "success" | "warning" | "danger" {
  if (percentage >= 90) return "danger";
  if (percentage >= 70) return "warning";
  return "default";
}

const Progress = React.forwardRef<HTMLDivElement, ProgressProps>(
  ({ className, value, max = 100, label, showValue = false, size = "md", variant, indicatorClassName, ...props }, ref) => {
    const percentage = Math.min((value / max) * 100, 100);
    const autoVariant = variant || getVariantFromPercentage(percentage);

    return (
      <div ref={ref} className={cn("space-y-1.5", className)} {...props}>
        {(label || showValue) && (
          <div className="flex items-center justify-between">
            {label && (
              <span className="text-sm font-medium text-muted-foreground">{label}</span>
            )}
            {showValue && (
              <span className="text-sm font-medium text-foreground">
                {value}/{max}
              </span>
            )}
          </div>
        )}
        <div
          className={cn(
            "w-full bg-secondary rounded-full overflow-hidden",
            sizeClasses[size]
          )}
        >
          <div
            className={cn(
              "h-full transition-all duration-300 ease-out rounded-full",
              indicatorClassName || variantClasses[autoVariant]
            )}
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>
    );
  }
);

Progress.displayName = "Progress";

export { Progress };
